import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useRouterState, redirect } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { authAwareRefetchInterval } from '@siliconx/shared/query/queryClient';
import { Button } from '@siliconx/shared/ui/Button';
import { PinToggle } from '@siliconx/shared/ui/PinToggle';
import { usePinnedPaths } from '@siliconx/shared/ui/navPins';
import {
  LayoutDashboard,
  LogOut,
  Sun,
  Moon,
  Monitor,
  X,
  Pin,
  Search,
  PanelLeftClose,
  PanelLeftOpen,
  Circle,
  CircleCheckBig,
  Menu,
} from 'lucide-react';
import { cn } from '@siliconx/shared/ui/utils';
import { useAuth } from '../../lib/authContext';
import { onboardingPhase, type OnboardingPhase } from '../../lib/onboardingPhase';
import { OrgFeature, useOrgFeature } from '../../lib/orgFeatures';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import {
  applyThemeChoice,
  getStoredThemeChoice,
  resolveIsDark,
  type ThemeChoice,
} from '../../lib/theme';
import { useAddAssetWizardRail, WIZARD_STEP_HINTS } from '../../lib/addAssetWizardRail';
import { NotificationBell } from './NotificationBell';
import { SetupProgressChip } from './SetupProgressChip';
import { JourneyStrip } from './JourneyStrip';
import { NavCommandPalette, type PaletteEntry } from '@siliconx/shared/ui/NavCommandPalette';
import logoLight from '@siliconx/shared/brand/logo-light.svg';
import logoDark from '@siliconx/shared/brand/logo-dark.svg';
import logoMarkLight from '@siliconx/shared/brand/logo-mark-light.svg';
import logoMarkDark from '@siliconx/shared/brand/logo-mark-dark.svg';
import {
  CUSTOMER_NAV,
  groupByPillar,
  findActivePillar,
  navForRole,
  type NavEntry,
  type NavPillar,
} from './nav/customerNav';
import {
  COLLAPSE_STORAGE_KEY,
  readPillarMemory,
  writePillarMemory,
  resolvePillarClickTarget,
} from './nav/pillarMemory';

// SIL-717: CUSTOMER_NAV, groupByPillar, findActivePillar, navForRole live in
// ./nav/customerNav.ts (zero React imports); the pillar-memory helpers live
// in ./nav/pillarMemory.ts (already pure). All importers repointed there
// directly — see their docstrings for why the split.

// Overview (was "Dashboard", SIL-542) is ungrouped (a single direct link) —
// it never has a panel to open, on hover or on click.
const DASHBOARD_PILLAR = 'Overview';
function pillarHasPanel(pillar: { pillar: string } | undefined): pillar is NavPillar {
  return !!pillar && pillar.pillar !== DASHBOARD_PILLAR;
}

// SIL-2139: `expanded` (desktop, not collapsed) widens the button to the full
// rail and left-aligns icon + label from `lg`; below `lg` it stays the
// icon-only square so the mobile rail (w-16) is unchanged.
function railBtnCls(active: boolean, expanded = false) {
  return cn(
    'relative flex size-10 items-center justify-center rounded-md transition-colors',
    expanded && 'lg:w-full lg:justify-start lg:gap-3 lg:px-2.5',
    active ? 'bg-brand-50 text-brand-700' : 'text-zinc-500 hover:bg-zinc-100 hover:text-zinc-700',
  );
}

/** Expanded-rail text label; desktop only (the mobile rail stays icon-only). */
function RailLabel({ children }: { children: ReactNode }) {
  return <span className="hidden truncate text-sm font-medium lg:inline">{children}</span>;
}

function panelItemCls(active: boolean) {
  return cn(
    'flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
    active ? 'bg-brand-50 font-medium text-brand-700' : 'text-zinc-700 hover:bg-zinc-100',
  );
}

/** Rendered inline (desktop expanded), as a flyout (desktop collapsed +
 * hover), and as a full-screen overlay (mobile) — same content, three
 * shells. */
function NavPanelBody({
  pillar,
  pinnedView,
  pinnedItems,
  pathname,
  opportunitiesBadge,
  onNavigate,
}: {
  pillar: NavPillar | undefined;
  pinnedView: boolean;
  pinnedItems: NavEntry[];
  pathname: string;
  opportunitiesBadge: number;
  onNavigate: () => void;
}) {
  const groups = pinnedView ? [{ heading: null, items: pinnedItems }] : (pillar?.groups ?? []);

  return (
    <nav className="px-3 py-4">
      <div className="mb-2 px-2 text-xs uppercase tracking-wider text-zinc-400">
        {pinnedView ? 'Pinned' : pillar?.pillar}
      </div>
      {pinnedView && pinnedItems.length === 0 && (
        <p className="px-2 py-4 text-sm text-zinc-400">
          No pinned pages yet — use the Pin toggle on any page to add one.
        </p>
      )}
      {groups.map((group, i) => (
        <div key={group.heading ?? `group-${i}`} className={i > 0 ? 'mt-4' : undefined}>
          {group.heading && (
            <div className="mb-1 px-3 text-xs uppercase tracking-wider text-zinc-400">{group.heading}</div>
          )}
          {group.items.map((item) => (
            <div key={item.to} className="group/nav-item flex items-center">
              <Link
                to={item.to}
                onClick={onNavigate}
                className={cn(panelItemCls(pathname === item.to), 'flex-1')}
              >
                <item.icon className="size-4" /> {item.label}
                {item.badge === 'opportunities' && opportunitiesBadge > 0 && (
                  <span className="ml-auto flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-xs font-medium leading-none text-white">
                    {opportunitiesBadge > 99 ? '99+' : opportunitiesBadge}
                  </span>
                )}
              </Link>
              {pinnedView && <PinToggle to={item.to} label={item.label} className="opacity-60 hover:opacity-100" />}
            </div>
          ))}
        </div>
      ))}
    </nav>
  );
}

/**
 * SIL-1305 - the first-run wizard checklist backing `onboardingPhase`
 * (lib/onboardingPhase.ts): Accept terms / Add an asset. SIL-1812: profile
 * completion moved to the Exchange, so it is no longer a setup step.
 * Replaces `NavPanelBody` (desktop inline panel + mobile drawer) entirely
 * while `phase !== 'done'` - see the call sites below.
 */
type SetupStepPhase = 'tos' | 'add-asset';
const SETUP_STEPS: { phase: SetupStepPhase; label: string }[] = [
  { phase: 'tos', label: 'Accept terms' },
  { phase: 'add-asset', label: 'Add an asset' },
];

const SETUP_PHASE_ORDER: OnboardingPhase[] = ['tos', 'add-asset', 'done'];

function setupStepStatus(
  stepPhase: SetupStepPhase,
  phase: OnboardingPhase,
): 'done' | 'current' | 'pending' {
  const stepIndex = SETUP_PHASE_ORDER.indexOf(stepPhase);
  const phaseIndex = SETUP_PHASE_ORDER.indexOf(phase);
  if (phaseIndex > stepIndex) return 'done';
  if (phaseIndex === stepIndex) return 'current';
  return 'pending';
}

/**
 * SIL-1812: the Add Asset wizard's own steps, nested under "Add an asset".
 * The in-page `AddAssetStepper` is hidden at `lg+` during setup, so this list
 * is the single visible home of the `progressbar` semantics there.
 */
function SetupWizardSteps() {
  const wizard = useAddAssetWizardRail();
  if (!wizard || wizard.steps.length === 0) return null;
  const { steps, interactive, onStepClick } = wizard;
  const currentIndex = steps.findIndex((st) => st.current);
  const stepNumber = currentIndex === -1 ? steps.length : currentIndex + 1;
  return (
    <div className="ml-6 mt-2">
      <div
        role="progressbar"
        aria-valuenow={stepNumber}
        aria-valuemin={1}
        aria-valuemax={steps.length}
        aria-valuetext={`Step ${stepNumber} of ${steps.length}`}
        aria-label="Add asset progress"
        className="mb-2 h-1 overflow-hidden rounded-full bg-muted"
      >
        <div
          className="h-full rounded-full bg-brand-600 transition-[width] duration-200 motion-reduce:transition-none"
          style={{ width: `${(stepNumber / steps.length) * 100}%` }}
        />
      </div>
      <ol aria-label="Add asset steps" className="space-y-1.5">
        {steps.map((step) => (
          <li key={step.id}>
            <button
              type="button"
              disabled={!interactive || !step.complete}
              aria-current={step.current ? 'step' : undefined}
              onClick={() => onStepClick(step.id)}
              className={cn(
                'flex w-full items-start gap-2 rounded-md px-1.5 py-1 text-left text-sm',
                step.complete && interactive ? 'hover:bg-zinc-100' : 'cursor-default',
              )}
            >
              {step.complete ? (
                <CircleCheckBig className="mt-0.5 size-3.5 shrink-0 text-emerald-600" aria-hidden="true" />
              ) : (
                <Circle
                  className={cn('mt-0.5 size-3.5 shrink-0', step.current ? 'text-brand-600' : 'text-zinc-300')}
                  aria-hidden="true"
                />
              )}
              <span className="min-w-0">
                <span
                  className={cn(
                    'block',
                    step.current ? 'font-medium text-zinc-900' : step.complete ? 'text-zinc-500' : 'text-zinc-400',
                  )}
                >
                  {step.label}
                </span>
                <span className="block text-xs text-zinc-400">{WIZARD_STEP_HINTS[step.id]}</span>
              </span>
            </button>
          </li>
        ))}
      </ol>
    </div>
  );
}

function SetupRail({ phase }: { phase: OnboardingPhase }) {
  return (
    <nav aria-label="Account setup" className="px-3 py-4">
      <div className="mb-3 px-2 text-xs uppercase tracking-wider text-zinc-400">Set up your account</div>
      <ol className="space-y-2">
        {SETUP_STEPS.map((step) => {
          const status = setupStepStatus(step.phase, phase);
          return (
            <li key={step.phase} className="px-2 py-1.5 text-sm">
              <div className="flex items-center gap-2">
                {status === 'done' ? (
                  <CircleCheckBig className="size-4 shrink-0 text-emerald-600" aria-hidden="true" />
                ) : (
                  <Circle
                    className={cn(
                      'size-4 shrink-0',
                      status === 'current' ? 'text-brand-600' : 'text-zinc-300',
                    )}
                    aria-hidden="true"
                  />
                )}
                <span
                  className={cn(
                    status === 'current'
                      ? 'font-medium text-zinc-900'
                      : status === 'done'
                        ? 'text-zinc-500'
                        : 'text-zinc-400',
                  )}
                >
                  {step.label}
                </span>
                <span className="sr-only">
                  {status === 'done' ? ' - complete' : status === 'current' ? ' - current step' : ' - not started'}
                </span>
              </div>
              {step.phase === 'add-asset' && status === 'current' && <SetupWizardSteps />}
            </li>
          );
        })}
      </ol>
      <p className="mt-4 px-2 text-xs text-zinc-400">
        After you add your first asset, you can track its value, see market prices, and buy or sell hardware.
      </p>
    </nav>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const router = useRouterState();
  const pathname = router.location.pathname;
  const navigate = useNavigate();
  const { logout, user, logoUrl, canSell, canBuy, organizationType, displayName, tosAccepted, hasAssets } =
    useAuth();
  // SIL-1305: single phase driving the setup rail below, NavCommandPalette's
  // entries, and the JourneyStrip/SetupProgressChip header gates — see
  // lib/onboardingPhase.ts.
  const phase = onboardingPhase({ tosAccepted, organizationType, canSell, canBuy, hasAssets });
  // SIL-1054: GET /api/client/opportunities is orgFeature: 'marketplaceOps'-
  // gated (opportunities.ts) — an individual org's single-unit scale has no
  // meaning for the unsolicited-offer flow this badge counts, and the route
  // 403s for one. `canSell` alone is not enough to gate this: individual
  // orgs can sell too.
  const canOpportunities = useOrgFeature('marketplaceOps');

  // SIL-171: opportunities nav badge — same "new since last viewed" count the
  // page itself shows; POST /opportunities/mark-seen (fired on page open)
  // clears it, so this refetches after route changes too.
  const { data: opportunities } = useQuery({
    queryKey: qk.opportunities.badge(),
    queryFn: async () => {
      const res = await api.api.client.opportunities.$get({ query: { limit: '1' } });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      return res.json();
    },
    enabled: canSell && canOpportunities,
    // SIL-667: was a bare 60_000 — fired forever every minute even against
    // an expired token. Stops once this query is failing on auth; resumes
    // automatically once it next succeeds (see createAppQueryClient).
    refetchInterval: authAwareRefetchInterval(60_000),
  });
  const opportunitiesBadge = opportunities?.newCount ?? 0;

  // RUF-540: only admin-only self-service items are filtered from nav; every
  // seller/buyer surface always renders regardless of profile completeness —
  // an incomplete profile shows a locked state on the page itself.
  const isAdmin = user?.role === 'admin';
  // SIL-994: individual orgs never see the Team nav entry, and Settings
  // routes to the personal profile editor instead of the business one.
  const visibleNav = useMemo(
    () => navForRole(isAdmin, organizationType),
    [isAdmin, organizationType],
  );
  const pillars = useMemo(() => groupByPillar(visibleNav), [visibleNav]);

  const pinnedPaths = usePinnedPaths();
  const pinnedItems = useMemo(
    () => visibleNav.filter((item) => pinnedPaths.includes(item.to)),
    [visibleNav, pinnedPaths],
  );

  const activePillar = useMemo(() => findActivePillar(pillars, pathname), [pillars, pathname]);

  // SIL-566: record the current route as the active pillar's "last visited"
  // on every navigation — the write itself, not the click, is what makes
  // mid-pillar navigation (not just the initial rail click) count toward
  // memory.
  useEffect(() => {
    if (activePillar) writePillarMemory(activePillar.pillar, pathname);
  }, [activePillar, pathname]);

  // Collapse (rail-only mode) — default expanded, persisted (same pattern
  // as the existing siliconx-theme key).
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(COLLAPSE_STORAGE_KEY) === 'true';
  });
  function toggleCollapsed() {
    setCollapsed((prev) => {
      const next = !prev;
      window.localStorage.setItem(COLLAPSE_STORAGE_KEY, String(next));
      return next;
    });
  }

  const [pinnedViewActive, setPinnedViewActive] = useState(false);
  const [hoveredPillar, setHoveredPillar] = useState<string | null>(null);
  // Flyout vertical alignment (bugfix, regression from SIL-364/PR #815): the
  // flyout is a child of <aside>, which is its `relative` positioning
  // context, so the hovered rail button's `offsetTop` (its offset relative
  // to that same ancestor) is exactly the flyout's target `top`. Clamped to
  // a sane max so a pillar near the bottom of a tall rail doesn't push the
  // flyout below the viewport.
  const [hoveredPillarTop, setHoveredPillarTop] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  // `inert` isn't in this React/TypeScript version's JSX typings (React 18 +
  // @types/react 18.3), so it's set imperatively via ref rather than as a
  // JSX prop — see the drawer's own comment below for why it's needed at
  // all. Keeps the closed drawer's 9 tabbable controls (nav links, close
  // button) out of the tab order and hidden from assistive tech; removed the
  // instant `mobileOpen` flips true so focus can enter normally.
  const mobileDrawerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = mobileDrawerRef.current;
    if (!el) return;
    el.inert = !mobileOpen;
  }, [mobileOpen]);

  // ⌘K / Ctrl+K opens the nav search palette; Escape closes it (and, with
  // lower priority, the mobile nav drawer if the palette isn't open).
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      } else if (e.key === 'Escape' && paletteOpen) {
        setPaletteOpen(false);
      } else if (e.key === 'Escape' && mobileOpen) {
        setMobileOpen(false);
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [paletteOpen, mobileOpen]);

  function handlePillarClick(pillar: NavPillar) {
    setPinnedViewActive(false);
    setHoveredPillar(null);
    // SIL-566: prefer the pillar's remembered route (this session) over its
    // first item — see `resolvePillarClickTarget`'s docstring for the
    // staleness guard.
    const target = resolvePillarClickTarget(pillar, readPillarMemory()[pillar.pillar]);
    if (target) void navigate({ to: target });
    if (!pillarHasPanel(pillar)) return; // Dashboard: direct link, no panel
    setCollapsed(false);
    window.localStorage.setItem(COLLAPSE_STORAGE_KEY, 'false');
    setMobileOpen(true);
  }

  function handlePinnedButtonClick() {
    setPinnedViewActive((v) => !v);
    setHoveredPillar(null);
    setCollapsed(false);
    window.localStorage.setItem(COLLAPSE_STORAGE_KEY, 'false');
    setMobileOpen(true);
  }

  // SIL-548: three-state System / Light / Dark theme control. `themeChoice`
  // is the persisted pin ('system' when unset — see src/lib/theme.ts for the
  // storage contract); `dark` is the resolved boolean the rest of AppShell
  // renders against (logo swap, sun/moon icon, etc).
  const [themeChoice, setThemeChoice] = useState<ThemeChoice>(() => getStoredThemeChoice());
  // Initial value comes from the DOM, set synchronously by the index.html
  // pre-paint script — resolveIsDark() here would re-run the same logic a
  // beat later and risk a mismatch if this render happens after the OS
  // preference has already changed.
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));

  // Only in System mode: follow the OS live, not just at page load — a
  // two-state toggle can't express this, which is the whole point of W3.
  useEffect(() => {
    if (themeChoice !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    function onChange() {
      const isDark = mq.matches;
      setDark(isDark);
      document.documentElement.classList.toggle('dark', isDark);
    }
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [themeChoice]);

  // SIL-1529: the title is static and must never carry displayName or any
  // other identity field - GA4 and HubSpot send document.title (page_title)
  // with every hit, so a person's name or an org's legal name here would leak
  // PII to both. Reset it on mount in case a prior surface changed it.
  useEffect(() => {
    document.title = 'SiliconX';
  }, []);

  function selectTheme(choice: ThemeChoice) {
    setThemeChoice(choice);
    applyThemeChoice(choice);
    setDark(resolveIsDark(choice));
  }

  // SIL-1305: the palette lists no routes before the first-run wizard is
  // done — same predicate as the setup rail replacing the nav list below.
  const paletteEntries: PaletteEntry[] = useMemo(
    () =>
      phase !== 'done'
        ? []
        : visibleNav.map((item) => ({
            to: item.to,
            label: item.label,
            icon: item.icon,
            pillar: item.pillar,
            group: item.group,
          })),
    [visibleNav, phase],
  );

  const hoveredPillarData = hoveredPillar ? pillars.find((p) => p.pillar === hoveredPillar) : undefined;
  // SIL-1305: no hover flyout before setup is done — the desktop-collapsed
  // convenience over the nav list has nothing to show once that list is
  // replaced by the setup rail.
  const railVisible = phase === 'done';
  const flyoutVisible = phase === 'done' && collapsed && !!hoveredPillarData;

  return (
    <div className="flex h-dvh w-full overflow-hidden print:h-auto print:overflow-visible">
      {/* Mobile off-canvas panel scrim — covers the content area, not the
          rail (rail stays tappable to switch pillars while the panel is
          open, matching the "rail permanently visible" spec).

          `print:hidden` is load-bearing, and its absence was invisible on
          screen: a `position: fixed` element prints on page 1 ONLY, so a
          left-open scrim laid a full-height box over the whole passport
          page 1 and nothing else. It printed WHITE, not the 50% black it
          shows on screen — with "Background graphics" off Chrome drops the
          author `bg-black/50` but still paints the <button>'s UA button
          face, so the overlay was opaque and unattributable. Every other
          piece of shell chrome already carries `print:hidden`; this one
          did not. */}
      {mobileOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={() => setMobileOpen(false)}
          className={cn(
            'fixed inset-y-0 right-0 z-40 bg-black/50 lg:hidden print:hidden',
            railVisible ? 'left-16' : 'left-0',
          )}
        />
      )}

      {/* Icon rail - visible on every route once setup is done. SIL-1812: not
          rendered during setup (the setup panel is the only nav then). */}
      {railVisible && (
      <aside
        onMouseLeave={() => setHoveredPillar(null)}
        className={cn(
          'relative z-50 flex w-16 shrink-0 flex-col border-r border-zinc-200 bg-white print:hidden',
          !collapsed && 'lg:w-56',
        )}
      >
        {/* SIL-2139: expanded shows the full logo from `lg` (the mark below). */}
        <div
          className={cn(
            'flex h-14 items-center justify-center border-b border-zinc-200',
            !collapsed && 'lg:justify-start lg:px-4',
          )}
        >
          <Link to="/dashboard" aria-label="SiliconX home">
            <img
              src={dark ? logoMarkDark : logoMarkLight}
              alt=""
              className={cn('h-6 w-auto max-w-7 object-contain', !collapsed && 'lg:hidden')}
            />
            {!collapsed && (
              <img
                src={dark ? logoDark : logoLight}
                alt=""
                className="hidden h-6 w-auto max-w-32 object-contain lg:block"
              />
            )}
          </Link>
        </div>
        <nav
          className={cn(
            'flex flex-1 flex-col items-center gap-1 overflow-y-auto py-3',
            !collapsed && 'lg:px-2',
          )}
        >
          {pillars.map((pillar) => (
            <button
              key={pillar.pillar}
              type="button"
              onClick={() => handlePillarClick(pillar)}
              onMouseEnter={(e) => {
                setHoveredPillar(pillarHasPanel(pillar) ? pillar.pillar : null);
                setHoveredPillarTop(e.currentTarget.offsetTop);
              }}
              aria-label={pillar.pillar}
              title={pillar.pillar}
              aria-current={!pinnedViewActive && activePillar?.pillar === pillar.pillar ? 'page' : undefined}
              className={railBtnCls(!pinnedViewActive && activePillar?.pillar === pillar.pillar, !collapsed)}
            >
              <span className="flex h-5 w-5 items-center justify-center">
                <pillar.pillarIcon className="h-5 w-5" />
              </span>
              {!collapsed && <RailLabel>{pillar.pillar}</RailLabel>}
            </button>
          ))}
        </nav>
        <div
          className={cn(
            'flex flex-col items-center gap-1 border-t border-zinc-200 py-3',
            !collapsed && 'lg:px-2',
          )}
        >
          <button
            type="button"
            onClick={handlePinnedButtonClick}
            aria-label="Pinned pages"
            title="Pinned"
            aria-pressed={pinnedViewActive}
            className={railBtnCls(pinnedViewActive, !collapsed)}
          >
            <span className="flex h-5 w-5 items-center justify-center">
              <Pin className="h-5 w-5" />
            </span>
            {!collapsed && <RailLabel>Pinned</RailLabel>}
            {pinnedItems.length > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-600 px-1 text-xs font-medium leading-none text-white">
                {pinnedItems.length > 9 ? '9+' : pinnedItems.length}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            aria-label="Search pages (Cmd K)"
            title="Search pages (⌘K)"
            className={railBtnCls(false, !collapsed)}
          >
            <span className="flex h-5 w-5 items-center justify-center">
              <Search className="h-5 w-5" />
            </span>
            {!collapsed && <RailLabel>Search</RailLabel>}
          </button>
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-label={collapsed ? 'Expand navigation panel' : 'Collapse navigation panel'}
            title={collapsed ? 'Expand panel' : 'Collapse panel'}
            className={cn(railBtnCls(false, !collapsed), 'hidden lg:flex')}
          >
            {collapsed ? <PanelLeftOpen className="size-5" /> : <PanelLeftClose className="size-5" />}
            {!collapsed && <RailLabel>Collapse</RailLabel>}
          </button>
        </div>

        {/* Desktop hover flyout — only when the panel is collapsed; a DOM
            child of <aside> so hovering into it doesn't count as leaving
            the rail container (mouseleave only fires once the pointer
            actually leaves both). */}
        {flyoutVisible && (
          <div
            className="absolute left-full z-50 hidden w-64 border-r border-zinc-200 bg-white shadow-lg lg:block"
            style={{
              top:
                typeof window === 'undefined'
                  ? hoveredPillarTop
                  : Math.max(0, Math.min(hoveredPillarTop, window.innerHeight - 240)),
            }}
          >
            <NavPanelBody
              pillar={hoveredPillarData}
              pinnedView={false}
              pinnedItems={pinnedItems}
              pathname={pathname}
              opportunitiesBadge={opportunitiesBadge}
              onNavigate={() => setHoveredPillar(null)}
            />
          </div>
        )}
      </aside>
      )}

      {/* Desktop inline panel — expanded state. Dashboard has no panel.
          SIL-1305: while the first-run wizard isn't done, this panel is
          always shown (regardless of pinnedView/activePillar) and its body
          is the setup rail, not the nav list. */}
      {(phase !== 'done' || (!collapsed && (pinnedViewActive || pillarHasPanel(activePillar)))) && (
        <div className="hidden w-64 shrink-0 overflow-y-auto border-r border-zinc-200 bg-white lg:block print:hidden">
          {/* SIL-2139: once setup is done the expanded rail above carries the
              full logo, so this header is just the aligned divider. */}
          <div className="flex h-14 items-center border-b border-zinc-200 px-3">
            {phase !== 'done' && (
              <Link to="/dashboard" aria-label="SiliconX home">
                <img src={dark ? logoDark : logoLight} alt="" className="h-6 w-auto max-w-32 object-contain" />
              </Link>
            )}
          </div>
          {phase !== 'done' ? (
            <SetupRail phase={phase} />
          ) : (
            <NavPanelBody
              pillar={activePillar}
              pinnedView={pinnedViewActive}
              pinnedItems={pinnedItems}
              pathname={pathname}
              opportunitiesBadge={opportunitiesBadge}
              onNavigate={() => undefined}
            />
          )}
        </div>
      )}

      {/* Mobile full-screen off-canvas panel. Slides in from the left per
          DESIGN.md ("Below `lg` the rail becomes an overlay drawer - it
          slides from the left"). The closed state uses `-translate-x-full`
          (not `translate-x-full`) — this box is anchored `left-16 right-0`,
          so translating by its own width in the POSITIVE x direction pushed
          it fully past the right edge of the viewport and widened
          `document.scrollingElement.scrollWidth`, causing page-wide
          horizontal scroll on every route below `lg`. Translating negative
          tucks it behind the icon rail instead, which never extends the
          document width. `inert` + `aria-hidden` while closed keep its 9
          tabbable controls (links, close button) out of the tab order and
          hidden from assistive tech — previously they were fully focusable
          and announced despite being invisible. */}
      <div
        ref={mobileDrawerRef}
        aria-hidden={mobileOpen ? undefined : true}
        className={cn(
          'fixed inset-y-0 right-0 z-50 overflow-y-auto bg-white shadow-lg transition-transform duration-200 lg:hidden print:hidden',
          railVisible ? 'left-16' : 'left-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-14 items-center justify-between border-b border-zinc-200 px-4">
          <span className="text-sm font-medium text-zinc-700">
            {phase !== 'done' ? 'Setup' : pinnedViewActive ? 'Pinned' : (activePillar?.pillar ?? 'Menu')}
          </span>
          <Button variant="icon" aria-label="Close navigation" onClick={() => setMobileOpen(false)}>
            <X className="size-4" />
          </Button>
        </div>
        {/* SIL-1305: same setup-rail swap as the desktop inline panel above. */}
        {phase !== 'done' ? (
          <SetupRail phase={phase} />
        ) : (
          <NavPanelBody
            pillar={activePillar}
            pinnedView={pinnedViewActive}
            pinnedItems={pinnedItems}
            pathname={pathname}
            opportunitiesBadge={opportunitiesBadge}
            onNavigate={() => setMobileOpen(false)}
          />
        )}
      </div>

      {/* SIL: min-w-0 on this column overrides the flex-child default of
          min-width:auto, which otherwise lets a wide descendant (this
          header, in practice the theme/notification/setup control cluster)
          blow the whole column out past the viewport at mobile widths —
          documentElement.scrollWidth was 1161px at a 390px viewport before
          this fix. See AppShell mobile-overflow investigation. */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col print:overflow-visible">
        <header className="flex h-14 items-center justify-between border-b border-zinc-200 bg-white px-4 sm:px-6 print:hidden">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            {/* SIL-1812: no icon rail during setup, so on mobile this is the
                way to open the setup steps drawer. */}
            {!railVisible && (
              <Button
                variant="icon"
                className="lg:hidden"
                aria-label="Open setup steps"
                onClick={() => setMobileOpen(true)}
              >
                <Menu className="size-4" />
              </Button>
            )}
            {/* RUF-380: org logo (customer portal only) beside the org name. */}
            {logoUrl && (
              <img
                src={logoUrl}
                alt={displayName ?? 'Organization logo'}
                className="h-7 w-auto max-w-[140px] shrink-0 rounded object-contain"
              />
            )}
            <div className="min-w-0 truncate text-sm font-medium text-zinc-700">
              {displayName ?? 'SiliconX Management Portal'}
            </div>
          </div>
          <div className="flex min-w-0 shrink items-center gap-1">
            {/* SIL-1021/SIL-1034: financials/setup is now orgFeature:
                'finance'-gated (the whole Finance/ERP suite is) — an
                individual org's canSell would otherwise mount this chip and
                its query would 403 on every page. SIL-1305: also gated on
                the first-run wizard being done — same `phase` the setup
                rail reads. */}
            <OrgFeature name="finance">{canSell && phase === 'done' && <SetupProgressChip />}</OrgFeature>
            <NotificationBell />
            <div
              role="radiogroup"
              aria-label="Theme"
              className="flex items-center gap-0.5 rounded-md border border-zinc-200 p-0.5"
            >
              {(
                [
                  { choice: 'system', label: 'Use system theme', Icon: Monitor },
                  { choice: 'light', label: 'Use light theme', Icon: Sun },
                  { choice: 'dark', label: 'Use dark theme', Icon: Moon },
                ] as const
              ).map(({ choice, label, Icon }) => (
                <button
                  key={choice}
                  type="button"
                  role="radio"
                  aria-checked={themeChoice === choice}
                  title={label}
                  aria-label={label}
                  onClick={() => selectTheme(choice)}
                  className={cn(
                    'flex size-7 items-center justify-center rounded transition-colors',
                    themeChoice === choice
                      ? 'bg-brand-50 text-brand-700'
                      : 'text-zinc-500 hover:bg-zinc-100 hover:text-zinc-700',
                  )}
                >
                  <Icon className="size-4" />
                </button>
              ))}
            </div>
            <Button variant="icon"
              onClick={logout}
              title="Sign out"
              aria-label="Sign out"
            >
              <LogOut className="size-4" />
            </Button>
          </div>
        </header>
        {/* SIL-572 (I2-C5): Discover → Negotiate → Transact — page chrome
            above <main>, not route content. Renders null (no DOM, no
            reserved space) everywhere except the BUYING routes +
            /transactions — see JourneyStrip/journeyStrip.ts for the route
            list; own padding/print:hidden so it needs no wrapper.
            SIL-1305: also gated on the first-run wizard being done — same
            `phase` the setup rail reads. */}
        {phase === 'done' && <JourneyStrip />}
        <main className="min-h-0 flex-1 overflow-auto bg-zinc-50 p-4 sm:p-6 print:overflow-visible print:bg-white print:p-0">
          {children}
        </main>
        <footer
          data-track-surface="footer"
          className="flex h-10 shrink-0 items-center justify-end gap-4 border-t border-zinc-200 bg-white px-4 sm:px-6 print:hidden"
        >
          <Link to="/terms" data-track="footer_terms" className="text-xs text-zinc-500 hover:text-zinc-700">
            Terms of Service
          </Link>
          <Link to="/privacy" data-track="footer_privacy" className="text-xs text-zinc-500 hover:text-zinc-700">
            Privacy Policy
          </Link>
          {/* SIL-1529: reopens the HubSpot cookie banner via @siliconx/shared/analytics. */}
          <button
            type="button"
            data-cookie-settings=""
            data-track="footer_cookie_settings"
            className="text-xs text-zinc-500 hover:text-zinc-700"
          >
            Cookie settings
          </button>
        </footer>
      </div>

      <NavCommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        entries={paletteEntries}
        onNavigate={(to) => void navigate({ to })}
      />
    </div>
  );
}

// Re-export redirect helper so routes can use it without a direct tanstack import.
export { redirect };
